import { useState } from "react";
import { Modal } from "react-bootstrap";
import type { AdGroupNetworkChoice } from "@models/adGroup.model";
import { AD_GROUP_NETWORKS, useFeatureFlag } from "@/shared/featureFlags/useFeatureFlag";
import NetworkTag, { type NetworkLooks } from "./NetworkTag";

type Props = {
  mode: "create" | "edit";
  campaignName: string;
  initialName?: string;
  initialAudienceId?: number | null;
  audiences: { id: number; name: string }[];
  /** The networks switched on for the campaign, lower-case (MPN-1467). */
  campaignNetworks?: string[];
  networkLooks?: NetworkLooks;
  initialFollowCampaign?: boolean;
  /** The ad group's saved list, which choosing again restores. */
  initialNetworks?: string[];
  /** The server's reason for refusing the last save, shown in the form. */
  error?: string | null;
  isSaving: boolean;
  onClose: () => void;
  /** `networks` is null while the release toggle is off: the form says nothing about them. */
  onSubmit: (
    name: string,
    audienceDefinitionId: number | null,
    networks: AdGroupNetworkChoice | null,
  ) => void;
};

export default function AdGroupFormModal({
  mode,
  campaignName,
  initialName,
  initialAudienceId,
  audiences,
  campaignNetworks = [],
  networkLooks = {},
  initialFollowCampaign = true,
  initialNetworks = [],
  error,
  isSaving,
  onClose,
  onSubmit,
}: Props) {
  const choosesNetworks = useFeatureFlag(AD_GROUP_NETWORKS);
  const [name, setName] = useState(initialName ?? "");
  const [audienceId, setAudienceId] = useState<number | null>(initialAudienceId ?? null);
  const [followCampaign, setFollowCampaign] = useState(initialFollowCampaign);
  const [chosen, setChosen] = useState<string[]>(initialNetworks);

  /*
      Only the campaign's switched-on networks can be picked. A saved network the campaign has
      since switched off is still listed, so it can be unticked, but it cannot be ticked again --
      the server refuses it until the campaign switches it back on.
  */
  const networkOptions = [...new Set([...campaignNetworks, ...initialNetworks])];
  const toggleNetwork = (network: string) =>
    setChosen((current) =>
      current.includes(network) ? current.filter((n) => n !== network) : [...current, network],
    );
  const networksOk = !choosesNetworks || followCampaign || chosen.length > 0;

  /*
      An ad group serves ads to an audience — without one it targets nobody, so
      the audience is required rather than optional.
  */
  const hasAudiences = audiences.length > 0;
  const canSubmit = name.trim().length > 0 && audienceId != null && networksOk && !isSaving;

  const networkChoice = (): AdGroupNetworkChoice | null => {
    if (!choosesNetworks) return null;
    if (followCampaign) return { follow_campaign_networks: true };
    return { follow_campaign_networks: false, networks: [...chosen].sort() };
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem" }}>
          {mode === "create" ? "New ad group" : "Edit ad group"}
        </Modal.Title>
      </Modal.Header>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit(name.trim(), audienceId, networkChoice());
        }}
      >
        <Modal.Body>
          <div className="small text-muted mb-3">
            Campaign: <span className="fw-semibold">{campaignName}</span>
          </div>

          <label htmlFor="adGroupName" className="form-label small fw-semibold">
            Ad group name
          </label>
          <input
            id="adGroupName"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Treatment A"
            autoFocus
          />

          <label htmlFor="adGroupAudience" className="form-label small fw-semibold mt-3">
            Audience
          </label>
          <select
            id="adGroupAudience"
            className="form-select"
            value={audienceId ?? ""}
            onChange={(e) => setAudienceId(e.target.value ? Number(e.target.value) : null)}
            disabled={!hasAudiences}
          >
            <option value="" disabled>
              {hasAudiences ? "Choose an audience…" : "No audiences on this campaign"}
            </option>
            {audiences.map((audience) => (
              <option key={audience.id} value={audience.id}>
                {audience.name}
              </option>
            ))}
          </select>
          {!hasAudiences && (
            <div className="small text-muted mt-2">
              This campaign has no audiences yet. Create one in the Audience step first — an ad
              group needs one to know who it is serving.
            </div>
          )}

          {choosesNetworks && (
            <fieldset className="mt-3">
              <legend className="form-label small fw-semibold mb-1">Networks</legend>
              <div className="form-check">
                <input
                  id="adGroupFollowCampaign"
                  type="radio"
                  name="adGroupNetworkMode"
                  className="form-check-input"
                  checked={followCampaign}
                  onChange={() => setFollowCampaign(true)}
                />
                <label htmlFor="adGroupFollowCampaign" className="form-check-label small">
                  Follow campaign —{" "}
                  {campaignNetworks.length ? (
                    campaignNetworks.map((network, i) => (
                      <span key={network}>
                        {i > 0 && ", "}
                        <NetworkTag network={network} looks={networkLooks} />
                      </span>
                    ))
                  ) : (
                    <span className="text-muted">no networks switched on yet</span>
                  )}
                </label>
              </div>
              <div className="form-check">
                <input
                  id="adGroupChooseNetworks"
                  type="radio"
                  name="adGroupNetworkMode"
                  className="form-check-input"
                  checked={!followCampaign}
                  onChange={() => setFollowCampaign(false)}
                />
                <label htmlFor="adGroupChooseNetworks" className="form-check-label small">
                  Choose for this ad group
                </label>
              </div>
              <div className={`ms-4 mt-1 ${followCampaign ? "opacity-50" : ""}`}>
                {networkOptions.map((network) => {
                  const isOn = campaignNetworks.includes(network);
                  const checked = chosen.includes(network);
                  return (
                    <div key={network} className="form-check">
                      <input
                        id={`adGroupNetwork-${network}`}
                        type="checkbox"
                        className="form-check-input"
                        checked={checked}
                        disabled={followCampaign || (!isOn && !checked)}
                        onChange={() => toggleNetwork(network)}
                      />
                      <label htmlFor={`adGroupNetwork-${network}`} className="form-check-label small">
                        <NetworkTag network={network} looks={networkLooks} />
                        {!isOn && <span className="text-muted"> (switched off for this campaign)</span>}
                      </label>
                    </div>
                  );
                })}
                {!followCampaign && chosen.length === 0 && (
                  <div className="small text-muted">
                    Choose at least one network, or let the ad group follow the campaign.
                  </div>
                )}
              </div>
            </fieldset>
          )}

          {error && <div className="alert alert-danger py-2 small mt-3 mb-0">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={!canSubmit}>
            {mode === "create" ? "Create ad group" : "Save changes"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
